import { useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import { Moment } from 'moment';
import { inventoryProduct as inventoryProductApi } from 'utilities/api';
import catchCancel from 'helpers/catchCancel';
import formatInvoiceDateParam from 'screens/Inventory/helpers/formatInvoiceDateParam';

interface InvoiceTotalQuantityResponse {
  totalInvoicedQuantity: number | null;
  unit: string | null;
}

interface InvoiceTotalQuantityState {
  requestKey: string;
  totalInvoicedQuantity: number | null;
  unit: string | null;
  loading: boolean;
  error: unknown;
}

interface InvoiceTotalQuantityResult {
  totalInvoicedQuantity: number | null;
  unit: string | null;
  loading: boolean;
  error: unknown;
}

type InvoiceDate = Date | Moment | string;

interface InventoryProductApi {
  createChildApi: (options: {
    action: string;
  }) => {
    fetch: (
      id: null
    ) => {
      promise: Promise<AxiosResponse<InvoiceTotalQuantityResponse>>;
    };
  };
}

const useInventoryInvoiceTotalQuantity = (
  productId: string | undefined,
  cropSeasonIds: string[] | undefined,
  dateRange: InvoiceDate[] | null | undefined,
  enabled = false
): InvoiceTotalQuantityResult => {
  const requestIdRef = useRef(0);
  const cropSeasonIdsParam = cropSeasonIds?.join(',') ?? '';
  const startRangeDate = dateRange?.[0];
  const endRangeDate = dateRange?.[1];
  const hasCompleteDateRange = Boolean(startRangeDate && endRangeDate);
  const startDate = hasCompleteDateRange
    ? formatInvoiceDateParam(startRangeDate, false)
    : undefined;
  const endDate = hasCompleteDateRange
    ? formatInvoiceDateParam(endRangeDate, true)
    : undefined;
  const requestKey = JSON.stringify([
    productId,
    cropSeasonIdsParam,
    startDate,
    endDate,
    enabled
  ]);
  const [requestState, setRequestState] = useState<InvoiceTotalQuantityState>({
    requestKey,
    totalInvoicedQuantity: null,
    unit: null,
    loading: enabled && Boolean(productId),
    error: null
  });

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled || !productId) {
      setRequestState({
        requestKey,
        totalInvoicedQuantity: null,
        unit: null,
        loading: false,
        error: null
      });
      return () => {
        requestIdRef.current += 1;
      };
    }

    const params = new URLSearchParams({
      ...(cropSeasonIdsParam ? { cropSeasonIds: cropSeasonIdsParam } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {})
    });
    const query = params.toString();
    const productApi = (inventoryProductApi as unknown) as InventoryProductApi;
    const { promise } = productApi
      .createChildApi({
        action: `product/${productId}/invoices/total-quantity${
          query ? `?${query}` : ''
        }`
      })
      .fetch(null);

    promise
      .then(response => {
        if (requestId !== requestIdRef.current) return;
        setRequestState({
          requestKey,
          totalInvoicedQuantity: response.data.totalInvoicedQuantity,
          unit: response.data.unit,
          loading: false,
          error: null
        });
      })
      .catch((error: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setRequestState({
          requestKey,
          totalInvoicedQuantity: null,
          unit: null,
          loading: false,
          error
        });
      })
      .catch(catchCancel);

    return () => {
      requestIdRef.current += 1;
    };
  }, [productId, requestKey, cropSeasonIdsParam, startDate, endDate, enabled]);

  const isCurrentRequest = requestState.requestKey === requestKey;
  return {
    totalInvoicedQuantity: isCurrentRequest
      ? requestState.totalInvoicedQuantity
      : null,
    unit: isCurrentRequest ? requestState.unit : null,
    loading: !isCurrentRequest || requestState.loading,
    error: isCurrentRequest ? requestState.error : null
  };
};

export default useInventoryInvoiceTotalQuantity;
